<!-- eslint-disable vue/require-explicit-emits -->
<template>
  <component
    :is="direction === 'vertical' ? 'iColumn' : 'iRow'"
    :class="{
      ...classList,
      'empty-layout': isEmpty && !hasABorder,
      'root-layout': isRootLayout,
      'layout': true,
    }"
    v-bind="{
      ...myLayoutProps,
      'data-identifier': identifier,
      'data-parent-id': identifier,
      'draggable': editMode && isDraggable(identifier, configuration) && !breakpoint
    }"
    :align="direction === 'vertical' ? align : verticalAlign"
    :vertical-align="direction === 'vertical' ? verticalAlign : align"
    v-on="myEventListeners"
  >
    <Teleport to="body">
      <DynamicComponentContextualMenu
        v-if="(isDynamicAppComponent ? true : canEditPublication)
          && showContextualMenu
          && contextMenuFixedPositionStyle.top
          && contextMenuFixedPositionStyle.left"
        v-bind="contextMenuAttrs"
        v-on="contextMenuEvents"
      />
    </Teleport>
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle left"
      @mousedown.stop="handleMouseDownResize($event, 'left')"
    />
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle right"
      @mousedown.stop="handleMouseDownResize($event, 'right')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle top"
      @mousedown.stop="handleMouseDownResize($event, 'top')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle bottom"
      @mousedown.stop="handleMouseDownResize($event, 'bottom')"
    />
    <slot />
  </component>
</template>

<script>
import {
  LayoutElementMixin,
} from "%/dynamic-components/render-engine/components/elements/layouts/layout-element-mixin";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import LayoutWrap from "%/dynamic-components/render-engine/options/layout-options/layout-wrap";
import RootLayoutPositionOptions from "%/dynamic-components/render-engine/bundles/root-layout-position-options";
import DynamicComponentContextualMenu
  from "%/dynamic-components/render-engine/components/DynamicComponentContextualMenu.vue";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import PositioningOptions from "%/dynamic-components/render-engine/bundles/positioning-options";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BackgroundPosition from "%/dynamic-components/render-engine/options/background-options/background-position";
import BackgroundSize from "%/dynamic-components/render-engine/options/background-options/background-size";
import BackgroundRepeat from "%/dynamic-components/render-engine/options/background-options/background-repeat";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import AnimationOptions from "%/dynamic-components/render-engine/bundles/animations-options";
import EffectsOptions from "%/dynamic-components/render-engine/bundles/effects-options";
import LayoutPositionOptions from "%/dynamic-components/render-engine/bundles/layout-position-options";
import LayoutAlign from "%/dynamic-components/render-engine/options/layout-options/layout-align";
import LayoutAlignVertical from "%/dynamic-components/render-engine/options/layout-options/layout-align-vertical";
import StaticLayoutDirection from "%/dynamic-components/render-engine/options/layout-options/static-layout-direction";
import AnimationEffectsOptions from "%/dynamic-components/render-engine/bundles/animation-effects-options";

export default {
  name: "Container",
  components: { DynamicComponentContextualMenu },
  mixins: [LayoutElementMixin],
  elementPanelSettings: {
    displayName: "Container",
  },
  data() {
    return {
      LayoutWrap,
      BaseOption,
      BackgroundRepeat,
      BackgroundPosition,
      BackgroundColor,
      BackgroundSize,
      LayoutAlign,
      LayoutAlignVertical,
      StaticLayoutDirection,
    };
  },
  deprecated: true,
  optionBundles: [
    PositioningOptions,
    LayoutPositionOptions,
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    AnimationOptions,
    AnimationEffectsOptions,
    new EffectsOptions({}, {
      "text-shadow": true,
    }),
    SpacingOptions,
    BackgroundOptions,
  ],
  computed: {
    direction() {
      return this.getOptionValue(StaticLayoutDirection);
    },
    myAlign() {
      return this.direction === "vertical" ? this.align : this.verticalAlign;
    },
    align() {
      const value = this.getOptionValue(LayoutAlign);
      if (this.getOptionValue(StaticLayoutDirection) === "vertical") {
        switch (value) {
          case "start":
            return "left";
          case "end":
            return "right";
          default:
            return "center";
        }
      } else {
        switch (value) {
          case "start":
            return "top";
          case "end":
            return "bottom";
          default:
            return "middle";
        }
      }
    },
    verticalAlign() {
      const value = this.getOptionValue(LayoutAlignVertical);
      if (this.getOptionValue(StaticLayoutDirection) === "vertical") {
        switch (value) {
          case "start":
            return "top";
          case "end":
            return "bottom";
          case "center":
            return "middle";
          case "between":
            return "between";
          case "around":
            return "around";
          case "evenly":
            return "evenly";
          default:
            return "top";
        }
      } else {
        switch (value) {
          case "start":
            return "left";
          case "end":
            return "right";
          case "center":
            return "center";
          case "between":
            return "between";
          case "around":
            return "around";
          case "evenly":
            return "evenly";
          default:
            return "left";
        }
      }
    },
  },
  mounted() {
    if (this.editMode) {
      this.configureOptionsAndBuilder();
    }
  },
  methods: {
    configureOptionsAndBuilder() {
      if (this.isRootLayout) {
        this.setEditorElementConfig(this.configuration.identifier, "isDeletable", false);
        this.setEditorElementConfig(this.configuration.identifier, "isDraggable", false);
        this.setEditorElementConfig(this.configuration.identifier, "isDuplicatable", false);
        this.setEditorElementConfig(this.configuration.identifier, "canWrap", false);
        this.setEditorElementConfig(this.configuration.identifier, "canCreateCustomComponent", false);

        if (this.isPage) {
          this.setEditorElementOptionsConfig(this.configuration.identifier, {
            bundles: [
              new PositioningOptions({
                "default.positioning": {
                  position: "relative",
                  top: "unset",
                  right: "unset",
                  bottom: "unset",
                  left: "unset",
                },
              }),
              RootLayoutPositionOptions,
              new AppearanceOptions({}, {
                "visible": {
                  disable: true,
                  message: "Visibility can not be toggled for root containers",
                },
                "border-radius": false,
              }),
              SpacingOptions,
              BackgroundOptions,
            ],
          });
        }
      }
    },
  },
};
</script>
